/**
 * PhaseBoardView — E4-S4 REVIEW-section view.
 *
 * Consumes GET /gate-tracker?program_id=…[&student_id=…] (backend
 * landed in Batch A E6-S4) and renders a company/platoon grid of
 * per-trainee gate status. Row = trainee; columns = gates.
 *
 * The point of this view is to give a company commander (or higher)
 * a single glance at where the phase-line pressure is: who is stuck
 * on which gate.
 */

import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import { apiFetch } from '../providers/api-rest-provider';

interface GateStatus {
  gate_id: string;
  label: string;
  status: 'pass' | 'not-yet' | 'not-attempted' | 'blocked';
  score?: number | null;
  min_points?: number | null;
}

interface StudentGate {
  student_id: string;
  student_name?: string;
  student_group_id?: string;
  gates: GateStatus[];
}

interface GateTrackerResponse {
  program_id: string;
  gates: { gate_id: string; label: string }[];
  students: StudentGate[];
}

interface GateTrackerRequestState {
  key: string;
  data: GateTrackerResponse | null;
  loading: boolean;
  error: string | null;
}

const STATUS_COLOR: Record<GateStatus['status'], 'success' | 'warning' | 'default' | 'error'> = {
  pass: 'success',
  'not-yet': 'warning',
  'not-attempted': 'default',
  blocked: 'error',
};

const STATUS_ICON: Record<GateStatus['status'], string> = {
  pass: '✓',
  'not-yet': '…',
  'not-attempted': '–',
  blocked: '✗',
};

export function PhaseBoardView() {
  const [programId, setProgramId] = useState('');
  const program = programId.trim();
  const [request, setRequest] = useState<GateTrackerRequestState | null>(null);
  const currentRequest = request?.key === program ? request : null;
  const data = currentRequest?.data ?? null;
  const loading = currentRequest?.loading ?? Boolean(program);
  const error = currentRequest?.error ?? null;

  useEffect(() => {
    if (!program) return;
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setRequest({ key: program, data: null, loading: true, error: null });
      try {
        const raw = await apiFetch(
          `gate-tracker?program_id=${encodeURIComponent(program)}`,
          { signal: controller.signal },
        ) as GateTrackerResponse;
        if (controller.signal.aborted) return;
        if (raw.program_id !== program) {
          throw new Error('Phase-board response does not match the requested program.');
        }
        setRequest({ key: program, data: raw, loading: false, error: null });
      } catch (err) {
        if (controller.signal.aborted) return;
        setRequest({
          key: program,
          data: null,
          loading: false,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }, 300);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [program]);

  // Group students by their student_group_id so a company view is a
  // stack of platoon-scoped tables. Un-grouped students land in an
  // "(unassigned)" bucket.
  const groupedStudents = new Map<string, StudentGate[]>();
  for (const s of data?.students ?? []) {
    const key = s.student_group_id ?? '(unassigned)';
    let arr = groupedStudents.get(key);
    if (!arr) { arr = []; groupedStudents.set(key, arr); }
    arr.push(s);
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <ViewModuleIcon color="primary" />
        <Typography variant="h5">Phase boards</Typography>
        <Chip label="E4-S4" size="small" color="warning" />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Company / platoon gate-status roll-up, driven by the E6-S4 gate
        tracker. Row = trainee; column = gate; cell = pass / not-yet /
        blocked / not-attempted.
      </Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <TextField
            label="program_id"
            fullWidth
            value={programId}
            onChange={(e) => setProgramId(e.target.value)}
            helperText="Required. The program whose gates drive the board."
          />
        </CardContent>
      </Card>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {data && !loading && (
        <>
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
            <Chip label={`program: ${data.program_id}`} size="small" color="primary" />
            <Chip label={`gates: ${data.gates.length}`} size="small" />
            <Chip label={`trainees: ${data.students.length}`} size="small" />
            <Chip label={`groups: ${groupedStudents.size}`} size="small" />
          </Stack>

          {data.students.length === 0 && (
            <Alert severity="info">
              No trainees enrolled in this program. Add students via the STAFF section.
            </Alert>
          )}

          {[...groupedStudents.entries()].map(([groupId, students]) => (
            <Paper key={groupId} variant="outlined" sx={{ mb: 2, p: 1.5 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                {groupId === '(unassigned)' ? '(unassigned)' : `Group ${groupId}`}
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  · {students.length} trainee{students.length !== 1 ? 's' : ''}
                </Typography>
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Trainee</TableCell>
                    {data.gates.map((g) => (
                      <TableCell key={g.gate_id} align="center">{g.label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {students.map((s) => (
                    <TableRow key={s.student_id}>
                      <TableCell>{s.student_name ?? s.student_id}</TableCell>
                      {data.gates.map((g) => {
                        const cell = s.gates.find((x) => x.gate_id === g.gate_id);
                        if (!cell) return <TableCell key={g.gate_id} align="center">–</TableCell>;
                        return (
                          <TableCell key={g.gate_id} align="center">
                            <Chip
                              label={`${STATUS_ICON[cell.status]} ${cell.score != null ? cell.score : ''}`.trim()}
                              size="small"
                              color={STATUS_COLOR[cell.status]}
                              variant={cell.status === 'not-attempted' ? 'outlined' : 'filled'}
                              title={cell.status}
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          ))}
        </>
      )}

      {!data && !loading && !error && !program && (
        <Alert severity="info">
          Enter a <code>program_id</code> above to load the phase board.
        </Alert>
      )}
    </Box>
  );
}
